import { useRefState } from '@/hooks/useRefState';
import { updatePresentation } from '@/requests';
import { Carousel } from '@mantine/carousel';
import { Box, Container, Flex, getGradient, Text, useMantineTheme } from '@mantine/core';
import { useDebouncedCallback, useElementSize } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { Annotations } from '../Annotations/Annotations';
import { Element } from './Element';
import { SlideLogo } from './SlideLogo';

export function Slides({
  presentation,
  presentationSlides,
  form,
  formValues,
  displayOnly,
  showAnnotations,
  setUpdateFunction,
  annotationSettingsRef,
  updateAnnotationSettings,
  closeAnnotations,
}) {
  const mantineTheme = useMantineTheme();
  const { ref: containerRef, width: containerWidth } = useElementSize();
  const [annotationsRef, annotations, setAnnotations] = useRefState({});

  const updatePresentationMutation = useMutation({
    mutationFn: updatePresentation,
    onSuccess: async () => {
      setUpdateFunction(false);
    },
  });

  const debouncedUpdatePresentation = useDebouncedCallback(() => {
    updatePresentationMutation.mutate({
      id: presentation.presentation_id,
      annotations: annotationsRef.current,
    });
  }, 1500);

  const updateAnnotations = (allAnnotations) => {
    setUpdateFunction(true);
    setAnnotations(allAnnotations);
    debouncedUpdatePresentation();
  };

  if (presentationSlides && presentationSlides.length) {
    return presentationSlides.map((slide, index) => (
      <Carousel.Slide
        key={index}
        style={{
          backgroundColor: formValues.background_color,
          backgroundImage:
            formValues.background_color_type === 'solid'
              ? 'none'
              : getGradient(
                  {
                    deg: formValues.background_color_gradient_angle,
                    from: formValues.background_color_gradient_from,
                    to: formValues.background_color_gradient_to,
                  },
                  mantineTheme
                ),
        }}
      >
        <Box
          w="100%"
          h="calc(100dvh - var(--app-shell-header-offset, 0rem))"
          pos="relative"
          style={{ overflow: 'auto' }}
        >
          {/* min-height: 100% so that container is vertically centred */}
          {/* height: auto so that absolute position child will take up 100% height */}
          <Flex justify="center" align="center" h="auto" mih="100%" pos="relative">
            {!displayOnly && (
              <Annotations
                key={index}
                presentation={presentation}
                slideIndex={index}
                closeAnnotations={closeAnnotations}
                annotationSettingsRef={annotationSettingsRef}
                updateAnnotationSettings={updateAnnotationSettings}
                updateAnnotations={updateAnnotations}
                annotationsRef={annotationsRef}
                showAnnotations={showAnnotations}
              />
            )}

            <SlideLogo
              form={form}
              presentation={presentation}
              formValues={formValues}
              containerWidth={containerWidth}
            />
            {/* Auto margins on flex item allow scrolling when flex item height is greater than parent height */}

            <Container
              h="auto"
              w="1400px"
              p="2rem"
              m="auto"
              style={{ overflow: 'hidden' }}
              ref={containerRef}
            >
              {slide.elements.map((item, index) => {
                return (
                  <Element
                    item={item}
                    key={index}
                    formValues={formValues}
                    containerWidth={containerWidth}
                  />
                );
              })}
            </Container>
          </Flex>
        </Box>
      </Carousel.Slide>
    ));
  }

  return (
    <Carousel.Slide key={0}>
      <Box
        w="100%"
        h="calc(100dvh - var(--app-shell-header-offset, 0rem))"
        pos="relative"
        style={{ overflow: 'auto' }}
      >
        {/* min-height: 100% so that container is vertically centred */}
        {/* height: auto so that absolute position child will take up 100% height */}
        <Flex justify="center" align="center" h="auto" mih="100%" pos="relative">
          <Container
            h="auto"
            w="1400px"
            p="2rem"
            m="auto"
            style={{ overflow: 'hidden' }}
            ref={containerRef}
          >
            <Text size="xl">
              Welcome to Slides Generator! Use the text editor on the left to start adding content
              to your presentation. You can also import content from Markdown, a .DOCX file or
              Notion.
            </Text>
          </Container>
        </Flex>
      </Box>
    </Carousel.Slide>
  );
}
